/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { PropertiesService } from '@/services/requests/housien-services/PropertiesService';

/** Lifecycle values the detail screen's actions menu can write. */
export type PropertyLifecycleStatus = 'active' | 'suspended' | 'sold' | 'archived';

interface UsePropertyStatusActionsOptions {
  agencyId: string;
  propertyId: string;
  /** Reloads the property record after a write lands. */
  onChanged: () => void;
  /** Leaves the detail screen after the property stops being reachable. */
  onRemoved: () => void;
}

/**
 * Writes the lifecycle changes offered by the detail screen's actions menu.
 *
 * Every action here is a single `PUT` on `classification`, except deleting,
 * which is the existing `DELETE`.
 *
 * @param options - Identifiers and the callbacks that follow a write.
 * @returns The action callbacks plus a shared saving flag.
 */
export function usePropertyStatusActions({
  agencyId,
  propertyId,
  onChanged,
  onRemoved,
}: UsePropertyStatusActionsOptions) {
  const { t } = useTranslation('features/properties');
  const [saving, setSaving] = useState(false);

  const write = useCallback(
    async (classification: Record<string, unknown>, successKey: string, removes = false) => {
      setSaving(true);
      try {
        await PropertiesService.updateProperty(
          propertyId,
          { classification } as never,
          agencyId,
        );
        toast.success(t(successKey));
        if (removes) onRemoved();
        else onChanged();
      } catch {
        toast.error(t('detailView.actions.saveError'));
      } finally {
        setSaving(false);
      }
    },
    [agencyId, propertyId, t, onChanged, onRemoved],
  );

  const setStatus = useCallback(
    (status: PropertyLifecycleStatus, successKey: string, removes = false) =>
      write({ status }, successKey, removes),
    [write],
  );

  const suspend = useCallback(
    () => setStatus('suspended', 'detailView.actions.suspendedDone'),
    [setStatus],
  );

  const markSold = useCallback(
    () => setStatus('sold', 'detailView.actions.soldDone'),
    [setStatus],
  );

  // Archiving takes the listing out of the default portfolio read; the
  // record stays and comes back through the archived filter.
  const archive = useCallback(
    () => setStatus('archived', 'detailView.actions.archivedDone', true),
    [setStatus],
  );


  // The one write a terminal listing accepts. Everything else on it is
  // refused by the repository, which is why the menu offers only this.
  const reactivate = useCallback(
    () => setStatus('active', 'detailView.actions.reactivatedDone'),
    [setStatus],
  );

  const remove = useCallback(async () => {
    setSaving(true);
    try {
      await PropertiesService.deleteProperty(agencyId, propertyId);
      toast.success(t('detailView.actions.deletedDone'));
      onRemoved();
    } catch {
      toast.error(t('detailView.actions.saveError'));
    } finally {
      setSaving(false);
    }
  }, [agencyId, propertyId, t, onRemoved]);

  return { saving, suspend, markSold, archive, reactivate, remove };
}
